import {
  getActiveDateLocaleTag
} from "./chunk-2J3DUGJJ.mjs";

// src/time.ts
var parseTime24 = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const meridiem = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { hour12, minute: Number.isFinite(m) ? m : 0, meridiem };
};
var toTime24 = (hour12, minute, meridiem) => {
  let h = hour12 % 12;
  if (meridiem === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};
var formatTimePicker = (hhmm) => {
  const { hour12, minute, meridiem } = parseTime24(hhmm);
  return `${hour12}:${String(minute).padStart(2, "0")} ${meridiem}`;
};
var MINUTES_PER_DAY = 24 * 60;
var addMinutesToHHmm = (hhmm, minutes) => {
  const [h, m] = hhmm.split(":").map(Number);
  const from = (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
  const total = from + (Number.isFinite(minutes) ? minutes : 0);
  const wrapped = (total % MINUTES_PER_DAY + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return {
    time: `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`,
    nextDay: total >= MINUTES_PER_DAY
  };
};
var formatTimeRangePicker = (start, end) => {
  const from = parseTime24(start);
  const to = parseTime24(end);
  const left = from.meridiem === to.meridiem ? `${from.hour12}:${String(from.minute).padStart(2, "0")}` : formatTimePicker(start);
  return `${left} \u2013 ${formatTimePicker(end)}`;
};
var FALLBACK_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Lagos",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney",
  "Pacific/Auckland"
];
var TIMEZONES = (() => {
  const supported = Intl.supportedValuesOf;
  if (typeof supported === "function") {
    try {
      return supported("timeZone");
    } catch {
    }
  }
  return FALLBACK_TIMEZONES;
})();
var getTimezoneOffsetLabel = (tz, at = /* @__PURE__ */ new Date()) => {
  for (const timeZoneName of ["longOffset", "shortOffset"]) {
    try {
      const parts = new Intl.DateTimeFormat(void 0, {
        timeZone: tz,
        timeZoneName
      }).formatToParts(at);
      const label = parts.find((p) => p.type === "timeZoneName")?.value;
      if (label) return label;
    } catch {
    }
  }
  return "";
};
var getTimezoneOffsetMinutes = (tz, at = /* @__PURE__ */ new Date()) => {
  const m = getTimezoneOffsetLabel(tz, at).match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!m) return 0;
  const sign = m[1] === "-" ? -1 : 1;
  return sign * (Number(m[2]) * 60 + Number(m[3] ?? 0));
};
var getDeviceTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
var wallPartFormatters = /* @__PURE__ */ new Map();
var wallPartFormatterFor = (tz) => {
  const cached = wallPartFormatters.get(tz);
  if (cached) return cached;
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    });
    wallPartFormatters.set(tz, formatter);
    return formatter;
  } catch {
    return null;
  }
};
var wallPartsIn = (tz, at) => {
  const formatter = wallPartFormatterFor(tz);
  if (!formatter) return null;
  try {
    const parts = formatter.formatToParts(at);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    const y = get("year");
    const m = get("month");
    const d = get("day");
    const h = get("hour");
    const min = get("minute");
    if ([y, m, d, h, min].some((n) => !Number.isFinite(n))) return null;
    return { y, m, d, h, min };
  } catch {
    return null;
  }
};
var zonedWallTimeToUtc = (timeZone, year, month, day, hour, minute) => {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  if (!Number.isFinite(naive)) return null;
  const DAY_MS = 24 * 60 * 6e4;
  const offsets = [
    getTimezoneOffsetMinutes(timeZone, new Date(naive - DAY_MS)),
    getTimezoneOffsetMinutes(timeZone, new Date(naive + DAY_MS))
  ];
  let earliest = null;
  for (const offset of new Set(offsets)) {
    const candidate = new Date(naive - offset * 6e4);
    const parts = wallPartsIn(timeZone, candidate);
    if (!parts) return null;
    if (parts.y !== year || parts.m !== month || parts.d !== day || parts.h !== hour || parts.min !== minute) {
      continue;
    }
    if (!earliest || candidate.getTime() < earliest.getTime()) earliest = candidate;
  }
  return earliest;
};
var zonedWallTimeIso = (timeZone, date, time) => {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const clock = /^(\d{2}):(\d{2})$/.exec(time);
  if (!day || !clock) return null;
  const [y, m, d, h, min] = [day[1], day[2], day[3], clock[1], clock[2]].map(Number);
  const at = zonedWallTimeToUtc(timeZone, y, m, d, h, min);
  if (!at) return null;
  const offset = Math.round((Date.UTC(y, m - 1, d, h, min) - at.getTime()) / 6e4);
  const abs = Math.abs(offset);
  const pad = (value) => String(value).padStart(2, "0");
  return `${date}T${time}:00${offset < 0 ? "-" : "+"}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
};
var zonedWallTimeString = (timeZone, at) => {
  const parts = wallPartsIn(timeZone, at);
  if (!parts) return null;
  const pad = (value, width = 2) => String(value).padStart(width, "0");
  return `${pad(parts.y, 4)}-${pad(parts.m)}-${pad(parts.d)}T${pad(parts.h)}:${pad(parts.min)}`;
};
var zonedDateString = (timeZone, at) => {
  const parts = wallPartsIn(timeZone, at);
  if (!parts) return null;
  return `${String(parts.y).padStart(4, "0")}-${String(parts.m).padStart(2, "0")}-${String(parts.d).padStart(2, "0")}`;
};
var zonedTimeString = (timeZone, at) => {
  const parts = wallPartsIn(timeZone, at);
  if (!parts) return null;
  return `${String(parts.h).padStart(2, "0")}:${String(parts.min).padStart(2, "0")}`;
};
var formatTimeInZone = (at, timeZone) => {
  const date = typeof at === "string" ? new Date(at) : at;
  try {
    return new Intl.DateTimeFormat(getActiveDateLocaleTag(), {
      timeZone,
      hour: "numeric",
      minute: "2-digit"
    }).format(date);
  } catch {
    return "";
  }
};
var formatDateInZone = (at, timeZone) => {
  const date = typeof at === "string" ? new Date(at) : at;
  try {
    return new Intl.DateTimeFormat(getActiveDateLocaleTag(), {
      timeZone,
      month: "short",
      day: "numeric",
      year: "numeric"
    }).format(date);
  } catch {
    return "";
  }
};
var formatDateTimeInZone = (at, timeZone) => {
  const date = typeof at === "string" ? new Date(at) : at;
  try {
    return new Intl.DateTimeFormat(getActiveDateLocaleTag(), {
      timeZone,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(date);
  } catch {
    return "";
  }
};

export {
  parseTime24,
  toTime24,
  formatTimePicker,
  addMinutesToHHmm,
  formatTimeRangePicker,
  TIMEZONES,
  getTimezoneOffsetLabel,
  getTimezoneOffsetMinutes,
  getDeviceTimezone,
  zonedWallTimeToUtc,
  zonedWallTimeIso,
  zonedWallTimeString,
  zonedDateString,
  zonedTimeString,
  formatTimeInZone,
  formatDateInZone,
  formatDateTimeInZone
};
